import type { FormBrandOverride, ResolvedFormBrand } from '@engage/types';

const SYSTEM_DEFAULTS: ResolvedFormBrand = {
  logo_url: null,
  primary_color: '#1e1b4b',
  accent_color: '#3b5bdb',
  background_color: '#f4f5f9',
  text_color: '#2b2b3c',
  heading_font: 'Montserrat',
  body_font: 'Montserrat',
  button_radius_px: 8,
  content_width_px: 600,
  company_name: null,
};

/**
 * Resolve a brand against the org default + per-form sparse override.
 * The result is a flat record of every brand value, ready to apply as
 * CSS custom properties (see `brandToCssVars` below).
 *
 * The api also computes this at publish time (form-versions.service)
 * and snapshots onto FormVersion.brand_snapshot — the renderer can use
 * either, but at edit time we need to re-resolve live.
 */
export function resolveBrand(
  orgDefault: Partial<ResolvedFormBrand> | null | undefined,
  override: FormBrandOverride | null | undefined
): ResolvedFormBrand {
  return {
    ...SYSTEM_DEFAULTS,
    ...(orgDefault ?? {}),
    ...((override ?? {}) as Partial<ResolvedFormBrand>),
    company_name:
      override?.logo_url !== undefined
        ? orgDefault?.company_name ?? SYSTEM_DEFAULTS.company_name
        : orgDefault?.company_name ?? SYSTEM_DEFAULTS.company_name,
  };
}

/**
 * Returns a CSS custom-property style object usable as `style={...}` on
 * the form's root element. The renderer's stylesheet reads these vars,
 * so a single sheet handles every form.
 */
export function brandToCssVars(brand: ResolvedFormBrand): Record<string, string> {
  return {
    '--form-color-bg': brand.background_color,
    '--form-color-text': brand.text_color,
    '--form-color-primary': brand.primary_color,
    '--form-color-accent': brand.accent_color,
    '--form-radius': `${brand.button_radius_px}px`,
    '--form-content-width': `${brand.content_width_px}px`,
    '--form-heading-font': cssFont(brand.heading_font),
    '--form-body-font': cssFont(brand.body_font),
  };
}

function cssFont(font: string): string {
  return `"${font.replace(/"/g, '')}", system-ui, sans-serif`;
}
